export const toFiniteNumber = (value: unknown): number | undefined => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
};

export const getQuantityPrecision = (
  decimalPlaces?: number | null,
  factorAccountingSystem?: number | null,
) => {
  const explicitPrecision = toFiniteNumber(decimalPlaces);
  if (explicitPrecision !== undefined && explicitPrecision >= 0) {
    return Math.floor(explicitPrecision);
  }

  const factor = toFiniteNumber(factorAccountingSystem);
  if (!factor || factor <= 1) return 0;

  const log10 = Math.log10(factor);
  return Number.isInteger(log10) ? Math.max(0, log10) : 6;
};

export const getQuantityStepByUnit = (
  quantityStepByUnit?: number | null,
  decimalPlaces?: number | null,
  factorAccountingSystem?: number | null,
) => {
  const explicitStep = toFiniteNumber(quantityStepByUnit);
  if (explicitStep !== undefined && explicitStep > 0) return explicitStep;

  const precision = getQuantityPrecision(decimalPlaces, factorAccountingSystem);
  return precision > 0 ? Number(`0.${"0".repeat(precision - 1)}1`) : 1;
};

export const formatQuantityByUnit = (
  value: unknown,
  decimalPlaces?: number | null,
  factorAccountingSystem?: number | null,
) => {
  const numberValue = toFiniteNumber(value);
  if (numberValue === undefined) return "-";

  const precision = getQuantityPrecision(decimalPlaces, factorAccountingSystem);
  const minimumFractionDigits =
    precision > 0 && Math.abs(numberValue) > 0 && Math.abs(numberValue) < 1
      ? precision
      : 0;

  return numberValue.toLocaleString(undefined, {
    minimumFractionDigits,
    maximumFractionDigits: precision,
  });
};

export const accountingToByUnit = (
  accountingQuantity: unknown,
  factorAccountingSystem?: number | null,
) => {
  const quantity = toFiniteNumber(accountingQuantity);
  if (quantity === undefined) return undefined;

  const factor = toFiniteNumber(factorAccountingSystem);
  return factor && factor > 0 ? quantity / factor : quantity;
};

export const getPartitionMultiplier = (partition?: {
  percentage?: number | string | null;
}) => {
  const percentage = toFiniteNumber(partition?.percentage);
  return percentage !== undefined ? percentage / 100 : 1;
};

export const countAndPartitionToByUnit = (
  count: unknown,
  partition?: { percentage?: number | string | null },
) => {
  const countValue = toFiniteNumber(count);
  if (countValue === undefined) return undefined;
  return countValue * getPartitionMultiplier(partition);
};

export const quantityInputToByUnit = (
  quantityData?: {
    method?: string;
    count?: number;
    unitPartitionId?: number;
    availableWeight?: number;
  },
  product?: {
    unitPartition?: Array<{
      id: number;
      percentage?: number | string | null;
    }>;
  },
) => {
  if (!quantityData) return undefined;

  if (quantityData.method === "weight") {
    return toFiniteNumber(quantityData.availableWeight);
  }

  const partition = product?.unitPartition?.find(
    (item) => item.id === quantityData.unitPartitionId,
  );

  return countAndPartitionToByUnit(quantityData.count, partition);
};
